import { useCallback } from 'react';
import { newId } from './boardHelpers';
import { requireLocalCapacity } from '../../plans/localGate';
import * as boardsStore from '../../extensions/bundled/task-manager/boardsStore';
import { boardForNewTask } from '../../extensions/bundled/task-manager/taskNumbers';
import { addLabel, labelIdsOf } from '../../extensions/bundled/task-manager/boardLabels';
import { moveTaskToList } from '../../extensions/bundled/task-manager/moveTaskToList';
import { withDoneAt } from '../../extensions/bundled/task-manager/taskStatus';
// Shared with the documents extension deliberately, not duplicated: the
// attachment outbox consults isDocDeleted() and nothing else, so marking ids
// there is the ONLY way a deleted task stops its queued uploads. The guard is
// keyed by id alone and both features use crypto.randomUUID(), so a collision
// would need two different uuids to be equal.
import { markDocsDeleted } from '../../extensions/bundled/documents/deletedDocsGuard';

/**
 * Edits to the lists and tasks of the open board (add, rename, delete, move,
 * labels). Every edit goes through updateBoard and marks what it touched.
 * Moved out of TaskBoardTab unchanged.
 */
export default function useBoardTaskActions({
  boardRef,
  boardTeamId,
  currentUserId,
  deleteGroupTarget,
  flushIfTeamBoard,
  justCreatedGroupIdsRef,
  justCreatedTaskIdsRef,
  markDirtyEntity,
  // Plans v2: () => tasks on the user's own boards right now (sync).
  ownTaskCount,
  setDeleteGroupTarget,
  setSelectedTaskId,
  updateBoard,
}) {
  // "Add list" button: create an empty list and drop straight into its inline
  // rename (no modal). justCreatedGroupIdsRef makes the new Column/ListSection
  // auto-focus its rename input on mount.
  const handleAddListInline = useCallback(() => {
    const groupId = newId();
    justCreatedGroupIdsRef.current.add(groupId);
    updateBoard((prev) => ({
      ...prev,
      groups: [...prev.groups, { id: groupId, name: '', position: prev.groups.length }],
    }));
    markDirtyEntity(boardRef.current?.id, groupId);
    flushIfTeamBoard();
  }, [justCreatedGroupIdsRef, updateBoard, markDirtyEntity, boardRef, flushIfTeamBoard]);

  // Drop a card onto the "Add list" target: create a new unnamed list at the
  // end and move the card into it. Created silently (NOT in
  // justCreatedGroupIdsRef) so the drop doesn't immediately steal focus.
  const moveTaskToNewList = useCallback((taskId) => {
    let newGroupId = null;
    updateBoard((prev) => {
      const task = prev.tasks.find((t) => t.id === taskId);
      if (!task) return prev;
      const groupId = newId();
      newGroupId = groupId;
      const newGroup = { id: groupId, name: '', position: prev.groups.length };
      const remaining = prev.tasks
        .filter((t) => t.groupId === task.groupId && t.id !== taskId)
        .sort(boardsStore.compareTasksByPosition);
      const reindex = new Map(remaining.map((t, i) => [t.id, i]));
      const tasks = prev.tasks.map((t) =>
        t.id === taskId
          ? { ...t, groupId, position: 0 }
          : reindex.has(t.id)
            ? { ...t, position: reindex.get(t.id) }
            : t
      );
      return { ...prev, groups: [...prev.groups, newGroup], tasks };
    }, { history: false }); // part of a drag; committed as one entry at drag end
    if (newGroupId) markDirtyEntity(boardRef.current?.id, [newGroupId, taskId]);
  }, [updateBoard, markDirtyEntity, boardRef]);

  const handleDeleteGroup = useCallback(() => {
    if (!deleteGroupTarget) return;
    const groupId = deleteGroupTarget.id;
    setDeleteGroupTarget(null);
    let removedTaskIds = [];
    updateBoard((prev) => {
      removedTaskIds = prev.tasks.filter((t) => t.groupId === groupId).map((t) => t.id);
      return {
        ...prev,
        groups: prev.groups.filter((g) => g.id !== groupId),
        tasks: prev.tasks.filter((t) => t.groupId !== groupId),
      };
    });
    // The list's cards are local deletes too: a disk rebase must not re-add them.
    markDirtyEntity(boardRef.current?.id, [groupId, ...removedTaskIds]);
    flushIfTeamBoard();
  }, [deleteGroupTarget, setDeleteGroupTarget, updateBoard, markDirtyEntity, boardRef, flushIfTeamBoard]);

  const handleRenameGroup = useCallback(
    (groupId, name) => {
      const trimmed = (name || '').trim();
      if (!trimmed) return;
      updateBoard((prev) => ({
        ...prev,
        groups: prev.groups.map((g) => (g.id === groupId ? { ...g, name: trimmed } : g)),
      }));
      markDirtyEntity(boardRef.current?.id, groupId);
      flushIfTeamBoard();
    },
    [updateBoard, markDirtyEntity, boardRef, flushIfTeamBoard]
  );

  const handleAddTask = useCallback(
    (groupId) => {
      const add = () => {
        const id = newId();
        justCreatedTaskIdsRef.current.add(id);
        updateBoard((before) => {
          // Its key's number (APB-42, taskNumbers.js): older tasks are numbered
          // first, then this one gets the next number and taskSeq moves with it.
          const { board: prev, number } = boardForNewTask(before);
          const positionInGroup = prev.tasks.filter((t) => t.groupId === groupId).length;
          const now = new Date().toISOString();
          const task = {
            id,
            groupId,
            position: positionInGroup,
            title: null,
            descriptionHtml: '',
            priority: 'medium',
            // Explicit null, never undefined: the task is serialised straight
            // into the record's `data` map, and an undefined field disappears
            // through JSON — an assignee cleared to undefined would never clear
            // on any other device.
            assigneeId: null,
            // Explicit empty array for the same reason assigneeId is explicit
            // null: the task is serialised straight into the record data, and a
            // missing key is not the same as an empty one on the merge path.
            labels: [],
            // Owner defaults to whoever created the task and stays changeable
            // from the detail modal. It is deliberately NOT the assignee: the
            // person who raised a task keeps caring about it after handing the
            // work over, and that is exactly who the progress notifications are
            // for. Null on a personal board — there is nobody to tell.
            ownerId: boardTeamId ? String(currentUserId ?? '') || null : null,
            dueDate: null,
            createdAt: now,
            updatedAt: now,
            comments: [],
            // The checklist (task-manager/subtasks.js). Explicit for the same
            // reason as `labels`; readers still treat a missing key as [].
            subtasks: [],
            number,
          };
          return { ...prev, tasks: [...prev.tasks, task] };
        });
        markDirtyEntity(boardRef.current?.id, id);
        flushIfTeamBoard();
      };
      // Plans v2: a card on a personal board counts against the plan's task cap
      // (team boards don't); a full plan opens the upgrade instead.
      // `ownTaskCount` answers a number, or a Promise of one when the board
      // grid's summaries are not loaded yet (a board opened directly): then
      // the card is added once the count says it fits.
      if (!boardTeamId && typeof ownTaskCount === 'function') {
        const used = ownTaskCount();
        if (used && typeof used.then === 'function') {
          used
            .then((n) => {
              if (requireLocalCapacity('tasks', n)) add();
            })
            .catch((err) => console.warn('[boards] task count failed:', err));
          return;
        }
        if (!requireLocalCapacity('tasks', used)) return;
      }
      add();
    },
    [justCreatedTaskIdsRef, updateBoard, markDirtyEntity, boardRef, flushIfTeamBoard, boardTeamId, currentUserId, ownTaskCount]
  );

  const handleChangeTask = useCallback(
    (taskId, patch) => {
      updateBoard((prev) => {
        const now = new Date().toISOString();
        // A patch that closes or reopens the task also sets / clears its
        // done date ("done 10 Oct" on the card), whatever control sent it.
        return {
          ...prev,
          tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, ...withDoneAt(t, patch, now), updatedAt: now } : t)),
        };
      });
      markDirtyEntity(boardRef.current?.id, taskId);
      // descriptionHtml patches fire continuously while typing (Lexical's
      // OnChangePlugin has no built-in debounce) — flushing those would flood
      // the network on every keystroke. Every other patch shape (title-on-
      // blur, priority, dueDate, comments) is already a single settled edit.
      if (!('descriptionHtml' in patch)) flushIfTeamBoard();
    },
    [updateBoard, markDirtyEntity, boardRef, flushIfTeamBoard]
  );

  // A new label typed in the task window's Labels chip (owner, 06.10.2026): it joins the
  // board's palette — saved like an edit in board settings — and is switched on for the task.
  const handleAddLabelToTask = useCallback(
    (taskId, name) => {
      const id = boardRef.current?.id;
      if (!id) return;
      const labelId = newId();
      updateBoard((prev) => {
        const labels = addLabel(prev, { id: labelId, name });
        const created = labels.find((l) => l.name.toLowerCase() === name.trim().toLowerCase());
        if (!created) return prev;
        return {
          ...prev,
          labels,
          tasks: prev.tasks.map((t) =>
            t.id === taskId && !labelIdsOf(t).includes(created.id) ? { ...t, labels: [...labelIdsOf(t), created.id], updatedAt: new Date().toISOString() } : t
          ),
        };
      });
      markDirtyEntity(id, '__meta__');
      markDirtyEntity(id, taskId);
      flushIfTeamBoard();
    },
    [boardRef, updateBoard, markDirtyEntity, flushIfTeamBoard]
  );

  // "Move to" in the task modal (redesign 05.10.2026): to the end of the other list.
  const handleMoveTaskToList = useCallback(
    (taskId, groupId) => {
      updateBoard((prev) => ({ ...prev, tasks: moveTaskToList(prev.tasks, taskId, groupId) }));
      markDirtyEntity(boardRef.current?.id, taskId);
      flushIfTeamBoard();
    },
    [updateBoard, markDirtyEntity, boardRef, flushIfTeamBoard]
  );

  const handleDeleteTask = useCallback(
    (taskId) => {
      // BEFORE the removal, and synchronously: an image still uploading for
      // this task would otherwise finish and leave ciphertext in the bucket
      // that nothing references and no UI can reach — the description that
      // held its key is gone.
      markDocsDeleted([taskId]);
      updateBoard((prev) => ({ ...prev, tasks: prev.tasks.filter((t) => t.id !== taskId) }));
      markDirtyEntity(boardRef.current?.id, taskId);
      setSelectedTaskId((cur) => (cur === taskId ? null : cur));
      flushIfTeamBoard();
    },
    [updateBoard, markDirtyEntity, boardRef, setSelectedTaskId, flushIfTeamBoard]
  );

  return { handleAddListInline, moveTaskToNewList, handleDeleteGroup, handleRenameGroup, handleAddTask, handleChangeTask, handleAddLabelToTask, handleMoveTaskToList, handleDeleteTask };
}
